/**
 * Petits utilitaires reprenant à l'identique la sémantique de lodash là où le natif diffère.
 */

/** Chaîne vide pour `null` / `undefined`, `String(value)` sinon (comme `_.toString`). */
const toText = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

/**
 * Vrai pour `null`, `undefined`, chaîne ou tableau vide, `Map` / `Set` vide, objet sans clé
 * propre énumérable, et pour tout nombre, booléen ou fonction (comme `_.isEmpty`).
 */
export const isEmpty = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
    if (value instanceof Map || value instanceof Set) return value.size === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return true;
};

/** Première lettre en majuscule, le reste en minuscules (comme `_.capitalize`). */
export const capitalize = (value: unknown): string => {
    const lower = toText(value).toLowerCase();
    const [first = ''] = lower;
    return first.toUpperCase() + lower.slice(first.length);
};

/**
 * Comparaison croissante de lodash : valeurs comparables d'abord, puis `null`, puis
 * `undefined`, puis `NaN`.
 */
const compareAscending = (value: unknown, other: unknown): number => {
    if (value === other) return 0;

    const valIsDefined = value !== undefined;
    const valIsNull = value === null;
    const valIsReflexive = value === value;
    const othIsDefined = other !== undefined;
    const othIsNull = other === null;
    const othIsReflexive = other === other;
    const a = value as string | number;
    const b = other as string | number;

    if ((!othIsNull && a > b) || (valIsNull && othIsDefined && othIsReflexive) || (!valIsDefined && othIsReflexive) || !valIsReflexive) {
        return 1;
    }
    if ((!valIsNull && a < b) || (othIsNull && valIsDefined && valIsReflexive) || (!othIsDefined && valIsReflexive) || !othIsReflexive) {
        return -1;
    }
    return 0;
};

/**
 * Tri stable sur une ou plusieurs propriétés (comme `_.orderBy`) : ordre `asc` par défaut,
 * collection absente traitée comme vide, tableau d'origine non modifié.
 */
export const orderBy = <T>(collection: readonly T[] | null | undefined, keys: readonly (keyof T)[], orders: readonly ('asc' | 'desc')[] = []): T[] =>
    (collection ?? [])
        .map((item, index) => ({ item, index }))
        .sort((left, right) => {
            for (const [position, key] of keys.entries()) {
                const result = compareAscending(left.item[key], right.item[key]);
                if (result !== 0) return orders[position] === 'desc' ? -result : result;
            }
            return left.index - right.index;
        })
        .map(({ item }) => item);
